import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, ListChecks, User, CalendarClock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const UNASSIGNED = "__none__";

const fmtDate = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

// Returns a flag descriptor for an incomplete task based on its due date.
const dueFlag = (item) => {
  if (!item.due_date) return null;
  if (item.done) return { text: `Due ${fmtDate(item.due_date)}`, cls: "text-[#9b938b]", warn: false };
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(item.due_date);
  const due = m ? new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59) : new Date(item.due_date);
  const days = Math.ceil((due - new Date()) / 86400000);
  if (days < 0) return { text: `Overdue · ${fmtDate(item.due_date)}`, cls: "bg-[#F6E3E3] text-[#9E4747]", warn: true };
  if (days <= 7) return { text: `Due soon · ${fmtDate(item.due_date)}`, cls: "bg-[#F5EAD9] text-[#8C7154]", warn: true };
  return { text: `Due ${fmtDate(item.due_date)}`, cls: "text-[#9b938b]", warn: false };
};

const ROLE_COLORS = { designer: "#A68A6B", team: "#8C7154", client: "#506B52" };

function ChecklistCard({ list, readOnly, people, onChange }) {
  const [text, setText] = useState("");
  const [assignee, setAssignee] = useState(UNASSIGNED);
  const [due, setDue] = useState("");
  const items = list.items || [];
  const doneCount = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const addItem = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    const person = people.find((p) => p.email === assignee);
    try {
      const { data } = await api.post(`/checklists/${list.id}/items`, {
        text: t,
        assignee_email: person?.email || null,
        assignee_name: person?.name || null,
        assignee_role: person?.role || null,
        due_date: due || null,
      });
      setText(""); setAssignee(UNASSIGNED); setDue("");
      onChange(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggle = async (item) => {
    try {
      const { data } = await api.put(`/checklists/${list.id}/items/${item.id}`, { done: !item.done });
      onChange(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeItem = async (item) => {
    const { data } = await api.delete(`/checklists/${list.id}/items/${item.id}`);
    onChange(data);
  };

  const removeList = async () => {
    await api.delete(`/checklists/${list.id}`);
    onChange(null, list.id);
  };

  return (
    <div className="border border-[#EBE5E0] bg-white" data-testid={`checklist-${list.id}`}>
      <div className="flex items-start justify-between gap-3 p-5 border-b border-[#F2EFEB]">
        <div className="min-w-0">
          <h4 className="font-serif text-xl text-[#2B2622] truncate">{list.name}</h4>
          <p className="text-[11px] uppercase tracking-wider text-[#A68A6B] mt-1">{doneCount}/{items.length} complete · {pct}%</p>
        </div>
        {!readOnly && (
          <button data-testid={`delete-checklist-${list.id}`} onClick={removeList}
            className="text-[#5C5651] hover:text-[#9E4747] shrink-0"><Trash2 size={16} strokeWidth={1.5} /></button>
        )}
      </div>

      <div className="h-1 bg-[#F2EFEB]">
        <div className="h-full bg-[#A68A6B] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="divide-y divide-[#F2EFEB]">
        {items.length === 0 ? (
          <p className="text-sm text-[#5C5651] p-5">No tasks yet.</p>
        ) : (
          items.map((item) => {
            const flag = dueFlag(item);
            return (
              <div key={item.id} className="flex items-start gap-3 px-5 py-3 group" data-testid={`checklist-item-${item.id}`}>
                <Checkbox checked={item.done} disabled={readOnly} onCheckedChange={() => !readOnly && toggle(item)}
                  data-testid={`toggle-item-${item.id}`}
                  className="mt-0.5 rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B] data-[state=checked]:border-[#A68A6B]" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.done ? "line-through text-[#9b938b]" : "text-[#2B2622]"}`}>{item.text}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    {item.assignee_name && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#5C5651]" data-testid={`item-assignee-${item.id}`}>
                        <User size={11} strokeWidth={1.75} style={{ color: ROLE_COLORS[item.assignee_role] || "#A68A6B" }} />
                        {item.assignee_name}
                      </span>
                    )}
                    {flag && (
                      <span data-testid={`item-due-${item.id}`}
                        className={`inline-flex items-center gap-1 text-[11px] tracking-wide ${flag.cls} ${flag.warn ? "px-1.5 py-0.5" : ""}`}>
                        {flag.warn ? <AlertTriangle size={11} strokeWidth={1.75} /> : <CalendarClock size={11} strokeWidth={1.75} />}
                        {flag.text}
                      </span>
                    )}
                    {item.done && item.done_by && (
                      <span className="text-[11px] text-[#506B52]" data-testid={`item-done-${item.id}`}>
                        ✓ Completed by {item.done_by} · {fmtDateTime(item.done_at)}
                      </span>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <button data-testid={`delete-item-${item.id}`} onClick={() => removeItem(item)}
                    className="text-[#C9BBAE] hover:text-[#9E4747] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5">
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {!readOnly && (
        <form onSubmit={addItem} className="p-3 border-t border-[#F2EFEB] bg-[#F9F8F6] space-y-2">
          <Input data-testid={`add-item-input-${list.id}`} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Task name…"
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm" />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid={`assignee-select-${list.id}`}
                className="flex-1 min-w-[130px] rounded-none border-[#EBE5E0] bg-white focus:ring-0 text-sm h-9">
                <SelectValue placeholder="Assign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {people.map((p) => (
                  <SelectItem key={p.email} value={p.email}>{p.name} · {p.role_label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" data-testid={`due-date-input-${list.id}`} value={due} onChange={(e) => setDue(e.target.value)}
              title="Due date"
              className="w-[150px] rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B] text-sm h-9" />
            <Button data-testid={`add-item-btn-${list.id}`} type="submit"
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white px-4 shrink-0 h-9">
              <Plus size={16} strokeWidth={1.5} />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function ChecklistsTab({ projectId, readOnly }) {
  const [lists, setLists] = useState([]);
  const [people, setPeople] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/checklists`).then((r) => setLists(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get(`/projects/${projectId}/assignable`).then((r) => setPeople(r.data)).catch(() => {});
  }, [projectId]);

  const createList = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/checklists`, { name: n });
      setLists((prev) => [...prev, data]);
      setName("");
      setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onChange = (updated, deletedId) => {
    if (deletedId) { setLists((prev) => prev.filter((l) => l.id !== deletedId)); return; }
    setLists((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };

  const overall = useMemo(() => {
    const all = lists.flatMap((l) => l.items || []);
    const done = all.filter((i) => i.done).length;
    const upcoming = all.filter((i) => !i.done && dueFlag(i)?.warn).length;
    return { total: all.length, done, upcoming };
  }, [lists]);

  return (
    <div className="max-w-5xl space-y-6" data-testid="checklists-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Tasks</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Checklists / Assignments</h3>
          {overall.total > 0 && (
            <p className="text-sm text-[#5C5651] mt-1">
              {overall.done} of {overall.total} complete across all lists
              {overall.upcoming > 0 && (
                <span className="text-[#9E4747]"> · {overall.upcoming} due soon or overdue</span>
              )}
            </p>
          )}
        </div>
        {!readOnly && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-checklist-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5 shrink-0">
                <Plus size={15} className="mr-2" /> Add List
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">New list</DialogTitle>
                <DialogDescription className="sr-only">Create a new checklist for this project</DialogDescription>
              </DialogHeader>
              <form onSubmit={createList} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5651]">List name</Label>
                  <Input data-testid="new-checklist-input" autoFocus required value={name}
                    onChange={(e) => setName(e.target.value)} placeholder="e.g. Punch List, Client To-Dos"
                    className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                </div>
                <Button data-testid="create-checklist-submit" type="submit" disabled={busy}
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                  {busy ? "Creating…" : "Create List"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {lists.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <ListChecks size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          No lists yet.{!readOnly && " Create one above to start assigning tasks."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {lists.map((l) => (
            <ChecklistCard key={l.id} list={l} readOnly={readOnly} people={people} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}
